import { Box } from "@mui/material";
import { Input } from "components";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm, useWatch } from "react-hook-form";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import toast from "react-hot-toast";
import {
  useCreateProductConfig,
  useGlobalStore,
  useUpdateProductConfig,
} from "controller";
import { Decimal } from "utilz/decimal";

const schema = yup.object({
  product_name: yup
    .string()
    .trim()
    .max(30, "Product name cannot exceed 30 characters")
    .required("Required"),
  product_type: yup
    .string()
    .max(30, "Product type cannot exceed 30 characters")
    .trim()
    .required("Required"),
  price: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .max(1000000, "Unit price cannot exceed 1,000,000")
    .typeError("Unit price must be a number")
    .required("Unit price is required"),
});

export const BillingProductFields = forwardRef(
  ({ data, closeDrawer, mode }: any, ref) => {
    useImperativeHandle(ref, () => ({
      saveButtonClick: () => {
        handleSubmit(async (data: any) => {
          const decimalPrice = new Decimal(data.price);
          const newData = {
            ...data,
            price: decimalPrice.toDecimalPlaces(2).mul(100).toNumber(),
          };
          try {
            if (mode?.method === "create") {
              await createProductConfig(newData);
              toast.success("Product added successfully");
            } else if (mode?.method === "update") {
              await updateProductConfig({
                data: newData,
                id: mode?.data?.mapping_id,
              });
              toast.success("Product updated successfully");
            }
            closeDrawer();
            reset();
          } catch (error: any) {
            toast.error(
              error?.message ? error?.message : "Something went wrong",
            );
          }
        })();
      },
      cancelButtonClick: () => {
        closeDrawer();
        reset();
      },
    }));

    const currency = useGlobalStore((state) => state.currency);

    const {
      handleSubmit,
      control,
      reset,
      formState: { errors },
      setValue,
      watch,
    } = useForm({
      defaultValues: {
        product_name: data?.product_name || "",
        product_type: data?.product_type || "",
        price: data?.price / 100 || "",
      },
      resolver: yupResolver(schema),
    });

    useEffect(() => {
      if (mode?.data) {
        setValue("product_name", mode?.data?.product_name);
        setValue("product_type", mode?.data?.product_type);
        setValue("price", mode?.data?.price / 100);
      } else {
        reset();
      }
    }, [mode?.data, setValue, closeDrawer]);

    const { mutateAsync: createProductConfig, isLoading } =
      useCreateProductConfig();
    const { mutateAsync: updateProductConfig } = useUpdateProductConfig();

    return (
      <Box
        sx={{
          padding: "24px",
          display: "flex",
          width: "296px",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Input
          label={"Name"}
          name="product_name"
          control={control}
          error={errors.product_name ? true : false}
          isResponsive={false}
          //@ts-ignore
          errorMessage={errors.product_name?.message}
        />
        <Input
          label={"Product Type"}
          name="product_type"
          control={control}
          error={errors.product_type ? true : false}
          isResponsive={false}
          //@ts-ignore
          errorMessage={errors.product_type?.message}
        />
        <Controller
          control={control}
          name="price"
          render={({ field }) => (
            <Input
              label={"Unit price"}
              name="price"
              placeholder={`${currency?.symbol || ""}0.00`}
              error={errors?.price ? true : false}
              isResponsive={false}
              errorMessage={errors?.price?.message}
              type="number"
              value={field.value}
              onChange={(e) => {
                if (e.target.value.includes(".")) {
                  const parts = e.target.value.split(".");
                  if (parts[1] && parts[1].length > 2) {
                    e.preventDefault();
                    return;
                  }
                }
                field.onChange(e);
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-"
                ) {
                  e.preventDefault();
                }
              }}
            />
          )}
        />
      </Box>
    );
  },
);
